import { fbGet, fbSet, fbSetFull } from "./firebase";
import { authedFetch } from "./api";
import { DEFAULT_NOTIF_SETTINGS } from "../constants";
import { isNative, platform, nativePushToken, nativePushPermission, nativePushDelete } from "./native";

export const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

let _fcmListenerRegistered = false;
let _notifSettings = null;

// ── تسجيل توكن الدفع لكل جهاز على حدة ────────────────────────────────────────
// كان التوكن يُخزَّن بمفتاح = معرّف المستخدم فقط داخل notifications/company_tokens،
// فالدخول بنفس الحساب من جهاز ثانٍ يدوس توكن الجهاز الأول → يتوقّف وصول الإشعارات
// للجهاز القديم (وهو مسكّر) بلا سبب ظاهر. المفتاح الآن = «معرّف المستخدم::معرّف الجهاز».
const DEVICE_KEY = "velorci_device_id";
const REFRESH_MS = 6 * 60 * 60 * 1000;   // نُحدّث التسجيل مرة كل 6 ساعات كحد أقصى
let _lastRegisterAt = 0;

export const getDeviceId = () => {
  try {
    let d = localStorage.getItem(DEVICE_KEY);
    if (!d) {
      d = "d" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem(DEVICE_KEY, d);
    }
    return d;
  } catch { return "d-nostorage"; }
};

const tokenKeyFor = (user) => `${user?.id || "company"}::${getDeviceId()}`;

// يحوّل الحقل المخزَّن (map جديد أو array قديم) إلى map موحّد {مفتاح: توكن}.
const tokensToMap = (raw) => {
  if (!raw) return {};
  if (Array.isArray(raw)) {
    const m = {};
    raw.filter(Boolean).forEach((t, i) => { m[`legacy${i}`] = t; });
    return m;
  }
  return { ...raw };
};

export const getNotifSettings = () => _notifSettings || DEFAULT_NOTIF_SETTINGS;
export const setNotifSettings = (s) => { _notifSettings = s; };

export const initFCM = async () => {
  try {
    if (!('Notification' in window)) return null;
    // بدون مفتاح VAPID لا يمكن إصدار توكن ويب إطلاقاً. كان الفشل صامتاً تماماً
    // (getToken يرمي، والـ catch أدناه يبتلعه ويرجّع null) فيبدو النظام سليماً بينما
    // لا يُسجَّل أي جهاز. نصرّح به بوضوح كي يظهر سبب التعطّل فوراً.
    if (!VAPID_KEY) {
      console.error('FCM: REACT_APP_VAPID_KEY غير مضبوط — إشعارات الويب معطّلة بالكامل. راجع .env.example');
      return null;
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
    const { getApps, getApp } = await import('firebase/app');
    const app = getApps().length ? getApp() : null;
    if (!app) return null;
    const messaging = getMessaging(app);
    const swReg = await navigator.serviceWorker.ready;
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
    if (!_fcmListenerRegistered) {
      _fcmListenerRegistered = true;
      // Foreground messages: the SW's onBackgroundMessage does NOT fire while the
      // page is focused, so nothing would be shown. Display the notification here
      // via the SW registration (same content-based tag → no duplicate with the
      // background path), and dispatch an event so the UI can refresh lists.
      onMessage(messaging, (payload) => {
        try {
          const n = payload.notification || {};
          const d = payload.data || {};
          const title = n.title || d.title || '';
          const body  = n.body  || d.body  || '';
          const orderNum = d.orderNum || '';
          if (title && Notification.permission === 'granted') {
            swReg.showNotification(title, {
              body,
              icon: '/icon-192.png',
              badge: '/icon-192.png',
              vibrate: [200, 100, 200],
              tag: orderNum ? `bekvam-order-${orderNum}` : `bekvam-${title}`.slice(0, 80),
              renotify: true,
              data: { ...d, link: d.link || '/' },
            });
          }
        } catch (_) {}
        window.dispatchEvent(new CustomEvent('fcm-message', { detail: payload }));
      });
    }
    return token || null;
  } catch (e) { console.error('FCM init:', e); return null; }
};

// يحفظ توكن هذا الجهاز في قائمة الشركة دون الدوس على أجهزة أخرى.
const saveCompanyToken = async (user, token) => {
  const key = tokenKeyFor(user);
  const data = await fbGet("notifications", "company_tokens");
  const next = tokensToMap(data?.tokens);
  // نظّف أي مفتاح قديم يحمل نفس التوكن (تسجيل سابق بمفتاح = معرّف المستخدم فقط)
  for (const [k, v] of Object.entries(next)) if (v === token && k !== key) delete next[k];
  next[key] = token;
  const meta = { ...(data?.meta || {}) };
  // نسجّل منصّة الجهاز مع التوكن: رسالة FCM لجهاز أندرويد/iOS تحتاج بلوك `notification`
  // كي يعرضها النظام، بينما الويب يعرضها بنفسه من الـ Service Worker. بدون هذا الوسم
  // تصل الإشعارات للتطبيق صامتة تماماً (data-only) ولا يراها المستخدم.
  meta[key] = {
    at: Date.now(),
    name: user?.name || user?.id || "",
    ua: (navigator.userAgent || "").slice(0, 120),
    platform: platform(),
  };
  for (const k of Object.keys(meta)) if (!(k in next)) delete meta[k];
  const unchanged = JSON.stringify(next) === JSON.stringify(tokensToMap(data?.tokens))
    && Date.now() - Number(data?.meta?.[key]?.at || 0) < REFRESH_MS;
  if (unchanged) return;
  await fbSetFull("notifications", "company_tokens", { tokens: next, meta });
};

// الموظفون: نجمع توكنات أجهزتهم بدل استبدال الأخير (نفس علّة الجهاز الثاني).
// `devices` يحمل منصّة كل جهاز (web/android/ios) ليُبنى شكل رسالة FCM الصحيح لكلٍّ منها.
const saveEmployeeToken = async (user, token) => {
  const d = await fbGet("notifications", user.id);
  const uniq = [...new Set([token, ...(Array.isArray(d?.tokens) ? d.tokens : []), d?.token].filter(Boolean))].slice(0, 5);
  const devices = { ...(d?.devices || {}) };
  devices[getDeviceId()] = { token, platform: platform(), at: Date.now() };
  // اطرح الأجهزة التي لم يعد توكنها ضمن القائمة (خرجت أو انتهت صلاحيتها).
  for (const [k, v] of Object.entries(devices)) if (!uniq.includes(v?.token)) delete devices[k];
  await fbSet("notifications", user.id, { token, tokens: uniq, devices, name: user.name || user.id });
};

// يحذف توكناً بعينه من قائمة الشركة (يُستخدم لتنظيف توكن السوبر-أدمن المتسرّب).
const pruneCompanyToken = async (token) => {
  const data = await fbGet("notifications", "company_tokens");
  const raw = data?.tokens;
  if (!raw || Array.isArray(raw)) return;
  const next = { ...raw };
  let changed = false;
  for (const [k, v] of Object.entries(next)) if (v === token) { delete next[k]; changed = true; }
  if (!changed) return;
  const meta = { ...(data?.meta || {}) };
  for (const k of Object.keys(meta)) if (!(k in next)) delete meta[k];
  await fbSetFull("notifications", "company_tokens", { tokens: next, meta });
};

// نقطة الدخول الوحيدة لتسجيل الدفع. تُستدعى عند الدخول (prompt=true مع تفاعل
// المستخدم) وعند كل فتح للتطبيق/عودته للمقدّمة (prompt=false، بلا أي نافذة إذن).
// بدون الاستدعاء المتكرّر يبقى التوكن مسجّلاً وقت الدخول فقط، فإذا دوّره المتصفح
// أو حذفه السيرفر كتوكن منتهٍ لا يعود أبداً → تتوقف الإشعارات نهائياً بلا سبب ظاهر.
export const registerPush = async (user, { prompt = false, force = false } = {}) => {
  try {
    if (!user) return null;
    const native = isNative();
    // داخل تطبيق الجوال لا وجود لـ Notification API ولا Service Worker: المسار كله
    // نيتف عبر @capacitor-firebase/messaging، وهو يرجّع توكن FCM (لا APNs) فيبقى
    // /api/send-notification والتخزين كما هما بالضبط.
    if (!native && !("Notification" in window)) return null;
    if (!prompt) {                                 // لا نطلب الإذن بلا تفاعل مباشر
      const granted = native
        ? (await nativePushPermission()) === "granted"
        : Notification.permission === "granted";
      if (!granted) return null;
    }
    if (!force && !prompt && Date.now() - _lastRegisterAt < REFRESH_MS) return null;
    const token = native ? await nativePushToken({ prompt }) : await initFCM();
    if (!token) return null;
    _lastRegisterAt = Date.now();
    if (user.role === "superadmin") { await pruneCompanyToken(token); return null; }
    if (user.role === "company" || user.role === "company_employee" || user.id === undefined) {
      await saveCompanyToken(user, token);
    } else {
      await saveEmployeeToken(user, token);
    }
    return token;
  } catch (e) { console.error("registerPush:", e); return null; }
};

// عند الخروج: احذف تسجيل هذا الجهاز فقط (لا أجهزة المستخدم الأخرى).
export const unregisterPush = async (user) => {
  try {
    if (!user) return;
    // على الجوال نُبطل توكن الجهاز في FCM أيضاً، وإلا بقي الجهاز يستقبل إشعارات
    // الشركة بعد الخروج حتى لو حُذف من القائمة (التوكن يبقى صالحاً في FCM).
    if (isNative()) await nativePushDelete();
    const key = tokenKeyFor(user);
    const legacyKey = user.id || "company";
    const data = await fbGet("notifications", "company_tokens");
    const raw = data?.tokens;
    if (!raw || Array.isArray(raw)) return;
    const next = { ...raw };
    const myToken = next[key];
    let changed = false;
    for (const k of Object.keys(next)) {
      if (k === key || k === legacyKey || (myToken && next[k] === myToken)) { delete next[k]; changed = true; }
    }
    if (!changed) return;
    const meta = { ...(data?.meta || {}) };
    for (const k of Object.keys(meta)) if (!(k in next)) delete meta[k];
    await fbSetFull("notifications", "company_tokens", { tokens: next, meta });
  } catch {}
};

// تشخيص حالة الإشعارات على هذا الجهاز (لشاشة إعدادات التنبيهات).
export const getPushStatus = async (user) => {
  const native = isNative();
  const supported = native
    ? true
    : (typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator);
  const permission = native
    ? await nativePushPermission()
    : (supported ? Notification.permission : "unsupported");
  const key = tokenKeyFor(user);
  const data = await fbGet("notifications", "company_tokens");
  const map = tokensToMap(data?.tokens);
  return {
    supported,
    permission,
    registered: !!map[key],
    token: map[key] || null,
    devices: Object.keys(map).length,
    updatedAt: Number(data?.meta?.[key]?.at || 0),
    platform: platform(),
    // المنصّة المسجَّلة على السيرفر لهذا الجهاز — إن خالفت `platform` (تطبيق موسوم web)
    // يبني السيرفر رسالة صامتة لا يعرضها أندرويد. زر «تفعيل» يعيد وسمه.
    registeredPlatform: data?.meta?.[key]?.platform || "",
    native,
    // إعداد ناقص على مستوى النشر لا على مستوى الجهاز — تعرضه الشاشة صراحةً بدل أن
    // يظهر التعطّل كأنه رفض إذن من المستخدم.
    vapidMissing: !native && !VAPID_KEY,
  };
};

// ── نداء الإرسال الموحّد ──────────────────────────────────────────────────────
//
// نداء **واحد موثّق** لكل حدث. لم يعد العميل يمرّر توكنات ولا يعرف منصّة أي جهاز:
// يطلب «جمهوراً» (الشركة / موظف / هذا الجهاز) والسيرفر يقرأ التوكنات من وثيقة
// الشركة، يبني شكل الرسالة لكل منصّة، يرسل دفعةً واحدة، وينظّف الميت منها.
//
// قبل هذا كانت الواجهة تنادي /api/send-notification مرة لكل جهاز بالتسلسل، وكل
// نداء يوقّع JWT ويعمل رحلة OAuth كاملة — ثوانٍ معلّقة في المتصفح عند كل إسناد.
// والأهم: كانت الدالة بلا مصادقة إطلاقاً، فأي توكن مسرَّب يكفي لمراسلة أي جهاز.
const pushRequest = async ({ audience, empId, token, title, body, data = {}, link = "/", settingKey = null }) => {
  try {
    const res = await authedFetch("/api/send-notification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audience, empId, token, title, body, data, link, settingKey }),
    });
    return res.status;
  } catch (e) { console.error("notify:", e); return null; }
};

// إرسال لجهاز بعينه (زر «تجربة إشعار هذا الجهاز»). السيرفر يتحقّق أن التوكن يخصّ
// شركة المُرسِل قبل الإرسال — لا يمكن مراسلة جهاز شركة أخرى بتوكن مسرَّب.
export const sendNotification = async (token, title, body, data = {}, link = "/") =>
  token ? pushRequest({ audience: "device", token, title, body, data, link }) : null;

// نفس إرسال «جهاز بعينه» لكن يرجّع جواب السيرفر كاملاً ({sent, failed, errors…}) —
// لزر التجربة في إعدادات التنبيهات: يفرّق بين «لم يُرسَل» (وسببه) و«أُرسل».
export const sendTestToDevice = async (token, title, body) => {
  try {
    const res = await authedFetch("/api/send-notification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audience: "device", token, title, body, link: "/" }),
    });
    const j = await res.json().catch(() => ({}));
    return { status: res.status, ...j };
  } catch (e) { return { status: 0, error: String(e?.message || e) }; }
};

export const showDirectNotification = (title, body) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  navigator.serviceWorker?.ready.then(reg => {
    reg.showNotification(title, {
      body,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      requireInteraction: true,
      vibrate: [200, 100, 200],
    });
  }).catch(()=>{});
};

// إشعار لكل أجهزة الشركة. الفحص المحلي للإعداد يوفّر رحلة شبكة، والسيرفر يفحصه
// ثانيةً على كل حال — فالإعداد يسري حتى على المسارات التي لا تمرّ من هنا (مسار
// الطلب الجديد في api/webhook.js).
export const notifyCompany = async (title, body, key = null, { data = {}, link = "/" } = {}) => {
  if (key && getNotifSettings()[key] === false) return;
  return pushRequest({ audience: "company", title, body, data, link, settingKey: key });
};

export const notifyEmployee = async (empId, title, body, key = null, { data = {}, link = "/" } = {}) => {
  if (!empId) return;
  if (key && getNotifSettings()[key] === false) return;
  return pushRequest({ audience: "employee", empId, title, body, data, link, settingKey: key });
};

const TEST_TITLE = "🔔 تجربة إشعار";
const TEST_BODY = "الإشعارات تعمل بشكل صحيح ✅";

export const testNotifyRecipient = async (recipient) => {
  if (recipient === "company") return notifyCompany(TEST_TITLE, TEST_BODY);
  const empSnap = await fbGet("bekvam", "employees");
  const empList = empSnap?.list || {};
  const targets = Object.entries(empList)
    .filter(([, emp]) => (emp.role || emp.type) === recipient)
    .map(([id]) => id);
  // نداء لكل موظف، لكنها متوازية الآن (كانت متتابعة داخل حلقة توكنات متتابعة أيضاً).
  await Promise.all(targets.map((id) => notifyEmployee(id, TEST_TITLE, TEST_BODY)));
};
